import React from 'react';
import { AlertTriangle, Clock, CheckCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const RealtimeDetectionFeed = ({ detections }) => {
  const getTriggerTypeLabel = (type) => {
    const labels = {
      minimum_wage_breach: 'Minimum Wage Breach',
      overtime_rate_violation: 'Overtime Rate Violation',
      leave_entitlement_breach: 'Leave Entitlement',
      contract_compliance_violation: 'Contract Compliance',
      nasfund_calculation_error: 'NASFUND Error',
      swt_calculation_error: 'SWT Error',
      working_hours_violation: 'Working Hours',
      payment_delay_violation: 'Payment Delay'
    };
    return labels?.[type] || type;
  };

  const getSourceLabel = (source) => {
    const labels = {
      payroll_processing: 'Payroll Processing',
      timesheet_validation: 'Timesheet Validation',
      contract_review: 'Contract Review',
      leave_calculation: 'Leave Calculation',
      manual_audit: 'Manual Audit',
      automated_scan: 'Automated Scan'
    };
    return labels?.[source] || source;
  };

  return (
    <div className="space-y-3 max-h-[600px] overflow-y-auto">
      {detections?.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500 opacity-50" />
          <p className="text-sm font-medium">No violations detected</p>
          <p className="text-xs mt-1">System is monitoring in real-time</p>
        </div>
      ) : (
        detections?.map((detection, index) => (
          <div key={index} className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            {/* Detection Header */}
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
                  <span className="text-sm font-semibold text-gray-900">
                    {getTriggerTypeLabel(detection?.triggerType)}
                  </span>
                </div>
                <p className="text-xs text-gray-600">{detection?.ruleViolated}</p>
              </div>
              {detection?.alertSent && (
                <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded">
                  Alert Sent
                </span>
              )}
            </div>

            {/* Detection Details */}
            <div className="grid grid-cols-2 gap-2 mb-2 text-xs">
              <div>
                <span className="text-gray-500">Source:</span>
                <span className="ml-1 font-medium text-gray-900">{getSourceLabel(detection?.detectionSource)}</span>
              </div>
              <div>
                <span className="text-gray-500">Affected:</span>
                <span className="ml-1 font-medium text-gray-900">{detection?.affectedEmployeeIds?.length} employee(s)</span>
              </div>
            </div>

            {/* Variance Information */}
            {detection?.varianceAmount && (
              <div className="p-2 bg-red-50 border border-red-200 rounded text-xs mb-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-700">Variance:</span>
                  <span className="font-bold text-red-600">
                    K{detection?.varianceAmount?.toFixed(2)}
                    {detection?.variancePercentage && (
                      <span className="ml-1">({detection?.variancePercentage?.toFixed(1)}%)</span>
                    )}
                  </span>
                </div>
              </div>
            )}

            {/* Timestamp */}
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <Clock className="h-3 w-3" />
              <span>
                {detection?.detectionTimestamp
                  ? formatDistanceToNow(new Date(detection?.detectionTimestamp), { addSuffix: true })
                  : 'Just now'}
              </span>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default RealtimeDetectionFeed;